import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { formatSAR } from "@/lib/i18n";
import { Package, Upload, FileCheck2, Plus, Pencil, Image as ImageIcon, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const empty = {
  name_en: "", name_ar: "", description_en: "", description_ar: "",
  base_price: 25, preview_image: "",
  category_en: "Resume", category_ar: "سيرة ذاتية", stock: 999, active: true,
};

export default function Products() {
  const { t, lang } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileInfo, setFileInfo] = useState({});
  const [editing, setEditing] = useState(null);  // product id being edited or 'new'
  const [form, setForm] = useState(empty);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({ en: [], ar: [] });
  const inputsRef = useRef({});

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("zayd_token")}` });

  const fetchAll = async () => {
    setLoading(true);
    const r = await api.get("/products");
    setProducts(r.data || []);
    setLoading(false);
    const info = {};
    await Promise.all((r.data || []).map(async (p) => {
      try { info[p.id] = (await api.get(`/admin/products/${p.id}/file/info`)).data; }
      catch { info[p.id] = { has_file: false }; }
    }));
    setFileInfo(info);
    try { setCategories((await api.get("/admin/categories")).data || { en: [], ar: [] }); } catch { /* */ }
  };

  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing("new"); setForm(empty); setImageFile(null); setImagePreview(""); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name_en: p.name_en, name_ar: p.name_ar,
      description_en: p.description_en || "", description_ar: p.description_ar || "",
      base_price: p.base_price, preview_image: p.preview_image,
      category_en: p.category_en || "", category_ar: p.category_ar || "",
      stock: p.stock || 999, active: p.active !== false, sku: p.sku,
    });
    setImageFile(null); setImagePreview("");
  };

  const onSave = async () => {
    if (!form.name_en.trim() || !form.name_ar.trim() || !form.base_price) {
      toast.error(lang === "ar" ? "املأ الحقول المطلوبة" : "Please fill required fields");
      return;
    }
    setSaving(true);
    try {
      let prodId = editing;
      if (editing === "new") {
        const { data } = await api.post("/admin/products", form);
        prodId = data.id;
      } else {
        await api.put(`/admin/products/${editing}`, form);
      }
      if (imageFile && prodId) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const res = await fetch(`${API}/admin/products/${prodId}/image`, {
          method: "POST", headers: authHeaders(), body: fd,
        });
        if (!res.ok) throw new Error("image upload failed");
      }
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm(lang === "ar" ? "حذف هذا المنتج؟" : "Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      fetchAll();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const onUploadDownload = async (productId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/admin/products/${productId}/file`, {
        method: "POST", headers: authHeaders(), body: fd,
      });
      if (!res.ok) throw new Error("failed");
      toast.success(lang === "ar" ? "تم رفع الملف" : "File uploaded");
      fetchAll();
    } catch (e) { toast.error("Upload failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.dashboard}</p>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1 flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            {t.products}
          </h1>
        </div>
        <Button onClick={openNew} data-testid="product-add-new" className="gap-2">
          <Plus className="h-4 w-4" />
          {lang === "ar" ? "إضافة منتج" : "Add product"}
        </Button>
      </div>

      <div className="bg-surface border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <Th>{lang === "ar" ? "صورة" : "Image"}</Th>
              <Th>SKU</Th>
              <Th>{t.name}</Th>
              <Th>{lang === "ar" ? "النوع" : "Type"}</Th>
              <Th>{lang === "ar" ? "السعر" : "Price"}</Th>
              <Th>{lang === "ar" ? "ملف التحميل" : "Download file"}</Th>
              <Th className="text-end">{t.actions}</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Loading...</td></tr>}
            {products.map((p) => {
              const info = fileInfo[p.id] || {};
              const imgSrc = p.preview_image?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.preview_image}` : p.preview_image;
              return (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-t border-border hover:bg-muted/30">
                  <Td><img src={imgSrc} alt="" className="h-12 w-10 rounded-md object-cover" /></Td>
                  <Td className="font-mono text-xs">{p.sku}</Td>
                  <Td>
                    <div className="font-medium">{lang === "ar" ? p.name_ar : p.name_en}</div>
                    <div className="text-xs text-muted-foreground">{lang === "ar" ? p.name_en : p.name_ar}</div>
                  </Td>
                  <Td className="text-muted-foreground">{lang === "ar" ? p.category_ar : p.category_en}</Td>
                  <Td className="font-semibold">{formatSAR(p.base_price, lang)}</Td>
                  <Td>
                    <input
                      type="file"
                      ref={(el) => (inputsRef.current[p.id] = el)}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadDownload(p.id, f); e.target.value = ""; }}
                      className="hidden"
                      data-testid={`file-input-${p.id}`}
                    />
                    <Button size="sm" variant={info.has_file ? "outline" : "default"} onClick={() => inputsRef.current[p.id]?.click()} data-testid={`upload-button-${p.id}`} className="gap-1">
                      <Upload className="h-3 w-3" />
                      {info.has_file ? t.replaceFile : t.uploadFile}
                    </Button>
                    {info.has_file && <span className="ms-2 inline-flex items-center gap-1 text-xs text-success"><FileCheck2 className="h-3 w-3" />{info.filename}</span>}
                  </Td>
                  <Td className="text-end">
                    <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} className="gap-1 me-2">
                      <Pencil className="h-3 w-3" />
                      {lang === "ar" ? "تعديل" : "Edit"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)} data-testid={`product-delete-${p.id}`} className="gap-1 text-destructive hover:text-destructive">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? (lang === "ar" ? "إضافة منتج جديد" : "Add new product") : (lang === "ar" ? "تعديل المنتج" : "Edit product")}</DialogTitle>
            <DialogDescription>{lang === "ar" ? "اسم، نوع، سعر، صورة" : "Name, type, price, image"}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{lang === "ar" ? "الاسم (إنجليزي)" : "Name (English)"} *</Label>
              <Input data-testid="edit-name-en" value={form.name_en} onChange={(e) => setForm({...form, name_en: e.target.value})} required />
            </div>
            <div>
              <Label>{lang === "ar" ? "الاسم (عربي)" : "Name (Arabic)"} *</Label>
              <Input data-testid="edit-name-ar" value={form.name_ar} onChange={(e) => setForm({...form, name_ar: e.target.value})} required />
            </div>
            <div>
              <Label>{lang === "ar" ? "السعر (ر.س)" : "Price (SAR)"} *</Label>
              <Input data-testid="edit-price" type="number" min="0" step="0.5" value={form.base_price} onChange={(e) => setForm({...form, base_price: parseFloat(e.target.value || 0)})} required />
            </div>
            <div>
              <Label>{lang === "ar" ? "النوع / التصنيف" : "Type / Category"}</Label>
              <Input
                data-testid="edit-category"
                list="cat-list-en"
                value={lang === "ar" ? form.category_ar : form.category_en}
                onChange={(e) => setForm(lang === "ar" ? {...form, category_ar: e.target.value} : {...form, category_en: e.target.value})}
                placeholder={lang === "ar" ? "اكتب أو اختر نوع" : "Type new or pick"}
              />
              <datalist id="cat-list-en">
                {(lang === "ar" ? categories.ar : categories.en).map((c) => <option key={c} value={c} />)}
              </datalist>
              <p className="text-[10px] text-muted-foreground mt-1">
                {lang === "ar" ? "يمكنك إنشاء نوع جديد بكتابة اسمه" : "Type a new type name to create it"}
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>{lang === "ar" ? "الوصف (إنجليزي)" : "Description (English)"}</Label>
              <Input data-testid="edit-desc-en" value={form.description_en} onChange={(e) => setForm({...form, description_en: e.target.value})} />
            </div>
            <div className="sm:col-span-2">
              <Label>{lang === "ar" ? "الوصف (عربي)" : "Description (Arabic)"}</Label>
              <Input data-testid="edit-desc-ar" value={form.description_ar} onChange={(e) => setForm({...form, description_ar: e.target.value})} />
            </div>
            <div className="sm:col-span-2">
              <Label>{lang === "ar" ? "صورة المنتج" : "Product image"}</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="h-24 w-24 rounded-md border border-border bg-muted overflow-hidden flex-shrink-0">
                  {imagePreview ? (
                    <img src={imagePreview} alt="" className="h-full w-full object-cover" />
                  ) : form.preview_image ? (
                    <img src={form.preview_image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.preview_image}` : form.preview_image} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 m-auto mt-9 text-muted-foreground" />
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  data-testid="edit-image-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setImageFile(f);
                      setImagePreview(URL.createObjectURL(f));
                    }
                  }}
                  className="text-sm"
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {lang === "ar" ? "JPG / PNG / WEBP. ستظهر فوراً للزبائن بعد الحفظ." : "JPG / PNG / WEBP. Will be live for customers right after save."}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-cancel">{lang === "ar" ? "إلغاء" : "Cancel"}</Button>
            <Button onClick={onSave} disabled={saving} data-testid="edit-save">{saving ? "..." : (lang === "ar" ? "حفظ" : "Save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-start font-medium text-muted-foreground py-3 px-4 text-xs uppercase tracking-wider ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`py-3 px-4 align-middle ${className}`}>{children}</td>;
